import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { io, Socket } from "socket.io-client";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { BASE_URL } from "../config";

/** Socket.IO expects origin without trailing slash */
function socketServerUrl(): string {
  return BASE_URL.replace(/\/+$/, "");
}

interface SocketContextType {
  socket: Socket | null;
  socketConnected: boolean;
  resetSocket: () => void;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  socketConnected: false,
  resetSocket: () => {},
});

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { signedAdmin } = useSelector((state: RootState) => state.admin);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  const connectSocket = useCallback(() => {
    const userId = signedAdmin?.id?.trim();
    const token = localStorage.getItem("jwt");

    if (!userId || !token) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      setSocket(null);
      setSocketConnected(false);
      return;
    }

    socketRef.current?.disconnect();

    const newSocket = io(socketServerUrl(), { transports: ["websocket"] });

    newSocket.on("connect", () => {
      newSocket.emit("setup", userId);
    });

    newSocket.on("connected", () => {
      setSocketConnected(true);
    });

    newSocket.on("disconnect", () => {
      setSocketConnected(false);
    });

    socketRef.current = newSocket;
    setSocket(newSocket);
  }, [signedAdmin?.id]);

  useEffect(() => {
    connectSocket();
    return () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
      setSocketConnected(false);
    };
  }, [connectSocket]);

  const resetSocket = () => {
    connectSocket();
  };

  return (
    <SocketContext.Provider value={{ socket, socketConnected, resetSocket }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);
